import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useMemo, useState } from 'react';
import { api } from '../api.ts';
import { msg } from '../app/actions.ts';
import { useNav } from '../app/nav.tsx';
import { useToast } from '../app/toast.tsx';
import { can, useMe, usePros } from '../lib/data.ts';
import { addDays, DOW_SHORT, dowIndex, first, nowLocal } from '../lib/format.ts';
import { isoLocal } from '../lib/format.ts';
import { CfgChips } from '../ui/cfg.tsx';
import { DateSelect } from '../ui/DateSelect.tsx';
import { Sheet } from '../ui/Sheet.tsx';
import { TimeSelect } from '../ui/TimeSelect.tsx';
import type { Block } from '../lib/agenda.ts';

const REASONS = ['Médico', 'Curso', 'Pessoal', 'Atestado', 'Reunião', 'Falta'];
const hm = (m: number) => `${String(Math.floor(m / 60)).padStart(2, '0')}:${String(m % 60).padStart(2, '0')}`;
const dm = (d: string) => `${DOW_SHORT[dowIndex(d)]!.toLowerCase()} ${d.slice(8)}/${d.slice(5, 7)}`;

// Bloqueios e ausências: algumas horas, um dia inteiro ou vários dias (férias, atestado, curso), com o motivo.
// Abre da agenda (botão de cadeado, ou toque num bloqueio) e das configurações da equipe.
export function BloqueioSheet({ professionalId, date, time }: { professionalId?: string | null; date?: string; time?: string }) {
  const nav = useNav();
  const toast = useToast();
  const qc = useQueryClient();
  const me = useMe().data;
  const allPros = usePros().data ?? [];
  const today = nowLocal().date;
  const canAll = can(me, 'agendaTodas');
  const pros = canAll ? allPros : allPros.filter((p) => p.id === me?.professionalId);
  const canSalon = can(me, 'config');
  const [who, setWho] = useState<string>(professionalId === null ? 'salon' : professionalId ?? me?.professionalId ?? pros[0]?.id ?? '');
  const [tipo, setTipo] = useState<'horas' | 'dia' | 'varios'>('horas');
  const [de, setDe] = useState(date && date >= today ? date : today);
  const [ate, setAte] = useState(date && date >= today ? date : today);
  const [ini, setIni] = useState<string | null>(time ?? null);
  const [fim, setFim] = useState<string | null>(time ? hm(Math.min(24 * 60 - 15, Number(time.slice(0, 2)) * 60 + Number(time.slice(3, 5)) + 60)) : null);
  const [motivo, setMotivo] = useState('');
  const [busy, setBusy] = useState(false);
  const target = who === 'salon' ? null : who;

  const list = useQuery({ queryKey: ['blocks', 'sheet', who], queryFn: () => api.get<Block[]>(`/blocks${target ? `?professionalId=${target}` : ''}`), enabled: !!who });
  const mine = useMemo(() => (list.data ?? []).filter((b) => (who === 'salon' ? b.professionalId === null : b.professionalId === target || b.professionalId === null)), [list.data, who, target]);

  const times = useMemo(() => { const out: string[] = []; for (let m = 6 * 60; m <= 23 * 60 + 45; m += 15) out.push(hm(m)); return out; }, []);
  const timeOk = tipo !== 'horas' || (!!ini && !!fim && fim > ini);
  const reasonOk = motivo.trim().length >= 2;

  async function save() {
    if (!reasonOk) { toast('Diga o motivo do bloqueio.', 'warn'); return; }
    setBusy(true);
    try {
      const r = await api.post<{ conflicts: Array<{ client: string; date: string; time: string }> }>('/blocks/range', {
        professionalId: target, date: de, dateTo: tipo === 'varios' ? ate : undefined,
        from: tipo === 'horas' ? ini : undefined, to: tipo === 'horas' ? fim : undefined, reason: motivo.trim(),
      });
      await Promise.all([qc.invalidateQueries({ queryKey: ['blocks'] }), qc.invalidateQueries({ queryKey: ['availability'] }), qc.invalidateQueries({ queryKey: ['agenda'] })]);
      nav.close();
      const hit = r.conflicts;
      toast(hit.length
        ? `Bloqueado. ${hit.length} ${hit.length > 1 ? 'horários marcados caem' : 'horário marcado cai'} nesse período: remarque ${hit.map((x) => `${first(x.client)} (${dm(x.date)} ${x.time})`).join(', ')}.`
        : 'Bloqueado. Esse período não aparece mais como livre.');
    } catch (e) { toast(msg(e), 'warn'); } finally { setBusy(false); }
  }
  async function remove(b: Block) {
    try {
      await api.post(`/blocks/${b.id}/remove`, {});
      await Promise.all([qc.invalidateQueries({ queryKey: ['blocks'] }), qc.invalidateQueries({ queryKey: ['availability'] })]);
      toast('Bloqueio removido. O horário voltou a ficar livre.');
    } catch (e) { toast(msg(e), 'warn'); }
  }
  const range = (b: Block) => {
    const s = isoLocal(b.startsAt), e = isoLocal(b.endsAt);
    const endsMidnight = e.time === '00:00';
    const lastDay = endsMidnight ? addDays(e.date, -1) : e.date;
    if (s.time === '00:00' && endsMidnight) return lastDay === s.date ? `${dm(s.date)} · dia inteiro` : `${dm(s.date)} a ${dm(lastDay)}`;
    return `${dm(s.date)} · ${s.time} às ${e.time}`;
  };
  const whoName = who === 'salon' ? 'Salão inteiro' : allPros.find((p) => p.id === who)?.name;

  return (
    <Sheet title="Bloquear ou registrar ausência" sub={whoName}
      actions={<><button className="cta" disabled={!timeOk || busy || !who} onClick={save}>{busy ? 'Salvando…' : 'Bloquear'}</button><button className="link-back" onClick={nav.close}>Fechar</button></>}>
      {(pros.length > 1 || canSalon) && <>
        <p className="gl-title first">De quem</p>
        <div className="chipset pro-pick">
          {pros.map((p) => <button key={p.id} className={who === p.id ? 'on' : ''} aria-pressed={who === p.id} onClick={() => setWho(p.id)}>{p.id === me?.professionalId ? 'Eu' : first(p.name)}</button>)}
          {canSalon && <button className={who === 'salon' ? 'on' : ''} aria-pressed={who === 'salon'} onClick={() => setWho('salon')}>Salão inteiro</button>}
        </div></>}
      <p className="gl-title">O que é</p>
      <CfgChips value={tipo} options={[['horas', 'Algumas horas'], ['dia', 'Dia inteiro'], ['varios', 'Vários dias']]} onChange={setTipo} />
      <p className="gl-title">{tipo === 'varios' ? 'Começa em' : 'Dia'}</p>
      <DateSelect label="Dia do bloqueio" value={de} today={today} onChange={(d) => { setDe(d); if (ate < d) setAte(d); }} />
      {tipo === 'varios' && <><p className="gl-title">Volta a atender depois de</p><DateSelect label="Último dia bloqueado" value={ate} today={today} min={de} onChange={setAte} shortcuts={false} /></>}
      {tipo === 'horas' && <>
        <p className="gl-title">Horário</p>
        <TimeSelect label="Começa" value={ini} onChange={(t) => { setIni(t); if (!fim || fim <= t) setFim(hm(Math.min(24 * 60 - 15, Number(t.slice(0, 2)) * 60 + Number(t.slice(3, 5)) + 60))); }} free={times} from="06:00" to="23:45" allowBusy={false} />
        <TimeSelect label="Termina" value={fim} onChange={setFim} free={times.filter((t) => !ini || t > ini)} from="06:15" to="23:45" allowBusy={false} />
        {ini && fim && fim <= ini && <p className="m-hint">O fim precisa ser depois do início.</p>}
      </>}
      <p className="gl-title">Motivo</p>
      <input className="text-in" value={motivo} maxLength={200} placeholder="Ex.: consulta médica, curso, atestado" aria-label="Motivo do bloqueio" onChange={(e) => setMotivo(e.target.value)} />
      <div className="chipset soft" style={{ marginTop: 8 }}>{REASONS.map((r) => <button key={r} className={motivo === r ? 'on' : ''} onClick={() => setMotivo(motivo === r ? '' : r)}>{r}</button>)}</div>

      <p className="gl-title">Já bloqueado{who === 'salon' ? ' no salão' : ''}</p>
      {list.isLoading ? <p className="m-hint">Carregando…</p> : !mine.length ? <p className="m-hint">Nenhum bloqueio daqui para frente.</p> : (
        <div className="gl bq-list">
          {mine.map((b) => (
            <div key={b.id} className="gl-row">
              <span><b>{b.reason || 'Bloqueio'}</b><small>{range(b)}{b.professionalId === null && who !== 'salon' ? ' · salão inteiro' : ''}</small></span>
              {(b.professionalId ? canAll || b.professionalId === me?.professionalId : canSalon) && <button className="gl-act" onClick={() => remove(b)} aria-label={`Remover bloqueio ${b.reason}`}>Remover</button>}
            </div>
          ))}
        </div>
      )}
    </Sheet>
  );
}
